import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { GetJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';

const initialState = {
  status: {
    observationData: STATE_STATUS.IDLE,
    geographicData: STATE_STATUS.IDLE,
    geographicAreaLines: STATE_STATUS.IDLE,
    geographicAreaPoints: STATE_STATUS.IDLE,
  },
  observationData: [],
  geographicData: [],
  geographicAreaLines: [],
  geographicAreaPoints: [],
};

export const selectAllObservations = (state) => state.avalanche.observationData;
export const selectAllGeographicTypes = (state) => state.avalanche.geographicData;
export const selectAllGeographicAreaLines = (state) => state.avalanche.geographicAreaLines;
export const selectAllGeographicAreaPoints = (state) => state.avalanche.geographicAreaPoints;
export const selectGeographicAreaLinesDd = (state) => getGeograhpicAreaLinesDd(state);
export const selectGeographicAreaPointsDd = (state) => getGeograhpicAreaPointsDd(state);
export const selectTargetPointsDd = (state) => getGeograhpicTargetPointsDd(state);

export const fetchAvalanceObservationData = createAsyncThunk('avalanche/fetchAvalanceObservationData', async () => {
  let url = REST_API.AVALANCHE + 'observationdata';
  const res = await GetJson(url);
  return await res.json();
});

export const fetchAvalanceGeographicData = createAsyncThunk('avalanche/fetchAvalanceGeographicData', async () => {
  let url = REST_API.AVALANCHE + 'geographicdata';
  const res = await GetJson(url);
  return await res.json();
});

export const fetchGeograhpicAreaLines = createAsyncThunk('avalanche/fetchGeograhpicAreaLines', async () => {
  let url = REST_API.AVALANCHE + 'geographicarealines';
  const res = await GetJson(url);
  return await res.json();
});

export const fetchGeograhpicAreaPoints = createAsyncThunk('avalanche/fetchGeograhpicAreaPoints', async () => {
  let url = REST_API.AVALANCHE + 'geographicareapoints';
  const res = await GetJson(url);
  return await res.json();
});

/**
 * SELECTORS
 */
export const getAvalancheObservations = createSelector(
  [(state) => state.avalanche.observationData, (_, typeName) => typeName],
  (allObservations, typeName) => {
    const filteredObservations = allObservations
      .filter(({ ObservationTypeName, IsActive }) => ObservationTypeName === typeName && IsActive)
      .sort((a, b) => {
        return a.DisplayOrder < b.DisplayOrder ? -1 : a.DisplayOrder > b.DisplayOrder ? 1 : 0;
      });

    return filteredObservations.map((obs) => {
      let obj = { label: obs.Value + ' - ' + obs.Meaning, value: obs.Value };

      return obj;
    });
  },
);

export const getAvalancheGeographicAreas = createSelector(
  [(state) => state.avalanche.geographicData, (_, typeName) => typeName],
  (allGeographicAreas, typeName) => {
    const filteredAreas =
      typeName === ''
        ? allGeographicAreas
        : allGeographicAreas
            .filter(({ AreaTypeName }) => AreaTypeName === typeName)
            .sort((a, b) => {
              return a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0;
            });

    return filteredAreas.map((area) => {
      return {
        label: area.Code.toString() + ' - ' + area.Name,
        value: area.Id,
        name: area.Name,
        code: area.Code,
        isDisabled: area.IsActive !== 'Y',
        parentId: area.ParentId,
      };
    });
  },
);

const findParentWithValue = (itemList, pList, searchValue) => {
  const obj = itemList.find((item) => item.value === searchValue);
  if (!obj) return null; // Value not found
  const prgm = pList.find((p) => p.Id === obj.parentId);
  if (prgm) return prgm.Id; // Found the root, return its value
  // Recursively search for the parent with a null parentId
  return findParentWithValue(itemList, pList, obj.parentId);
};

export const getAllGeographicAreas = createSelector(
  [(state) => state.avalanche.geographicData],
  (allGeographicAreas) => {
    const filteredAreas = allGeographicAreas
      .filter(
        ({ AreaTypeName }) =>
          AreaTypeName === 'AVALANCHE AREA' ||
          AreaTypeName === 'AVALANCHE FORECAST AREA' ||
          AreaTypeName === 'AVALANCHE PATH' ||
          AreaTypeName === 'AVALANCHE SUB AREA',
      )
      .sort((a, b) => {
        return a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0;
      });
    const programs = allGeographicAreas.filter(({ AreaTypeName }) => AreaTypeName === 'AVALANCHE PROGRAM');
    const areas = filteredAreas.map((area) => {
      return {
        label: area.Code.toString() + ' - ' + area.Name,
        value: area.Id,
        isDisabled: area.IsActive != 'Y',
        parentId: area.ParentId,
      };
    });
    const _areas = JSON.parse(JSON.stringify(areas));
    return areas.map((a) => {
      const prmId = findParentWithValue(_areas, programs, a.value);
      return {
        label: a.label,
        value: a.value,
        isDisabled: a.isDisabled,
        parentId: a.parentId,
        programId: prmId,
      };
    });
  },
);
export const getAvalancheGeographicPaths = createSelector(
  [(state) => state.avalanche.geographicData],
  (allGeographicAreas) => {
    const areas = allGeographicAreas
      .filter(({ AreaTypeName }) => AreaTypeName === 'AVALANCHE PATH')
      .map((area) => {
        let areaCode = area.Code.substr(area.Code.length - 5);
        areaCode = parseFloat((parseInt(areaCode) / 100).toFixed(2));
        return {
          label: areaCode.toString() + ' - ' + area.Name,
          value: area.Code,
          parentId: area.ParentId,
          areaCode: areaCode,
          latitude: area.Latitude,
          longitude: area.Longitude,
          areaId: area.Id,
          isDisabled: area.IsActive != 'Y',
        };
      })
      .sort((a, b) => {
        return a.areaCode - b.areaCode;
      });

    return areas;
  },
);

export const getGeograhpicAreaLinesDd = createSelector([(state) => state.avalanche.geographicAreaLines], (lines) => {
  return lines.map((x) => {
    return {
      value: x.Id,
      label: `${x.Name}`,
      areaId: x.AreaId,
      programId: x.ProgramId,
      isDisabled: x.IsActive != 'Y',
      longitude: x.Longitude,
      latitude: x.Latitude,
      name: x.Name,
      description: x.Description,
      geographicPointType: x.GeographicPointType,
    };
  });
});

export const getGeograhpicAreaPointsDd = createSelector([(state) => state.avalanche.geographicAreaPoints], (points) => {
  return points.map((x) => {
    // TODO: This should not be remapped here. Stores should contain the raw original data.
    //  It's shared across multiple pages and requires remapping before submitting it to the API
    return {
      value: x.Id,
      label: `${x.Name} - ${x.Description}`,
      name: x.Name,
      areaId: x.AreaId,
      programId: x.ProgramId,
      description: x.Description,
      areaCode: x.AreaCode,
      areaTypeName: x.AreaTypeName,
      pointTypeName: x.PointTypeName,
      isDisabled: x.IsActive != 'Y',
      longitude: x.Longitude,
      latitude: x.Latitude,
    };
  });
});

export const getGeograhpicTargetPointsDd = createSelector(
  [(state) => state.avalanche.geographicAreaPoints],
  (points) => {
    return points
      .filter(
        ({ PointTypeName, AreaTypeName }) =>
          PointTypeName === 'AVALANCHE CONTROL TARGET' && AreaTypeName === 'AVALANCHE PATH',
      )
      .map((x) => {
        return {
          value: x.Id,
          label: `${x.Name}`,
          areaId: x.AreaId,
          programId: x.ProgramId,
          areaCode: x.AreaCode,
          areaTypeName: x.AreaTypeName,
          pointTypeName: x.PointTypeName,
          isDisabled: x.IsActive != 'Y',
        };
      })
      .sort((a, b) => {
        return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
      });
  },
);

export const avalancheSlice = createSlice({
  name: 'avalanche',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchAvalanceObservationData.pending]: (state) => {
      state.status.observationData = STATE_STATUS.LOADING;
    },
    [fetchAvalanceObservationData.fulfilled]: (state, action) => {
      state.status.observationData = STATE_STATUS.SUCCESS;
      state.observationData = action.payload;
    },
    [fetchAvalanceObservationData.rejected]: (state, action) => {
      state.status.observationData = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchAvalanceGeographicData.pending]: (state) => {
      state.status.geographicData = STATE_STATUS.LOADING;
    },
    [fetchAvalanceGeographicData.fulfilled]: (state, action) => {
      state.status.geographicData = STATE_STATUS.SUCCESS;
      state.geographicData = action.payload;
    },
    [fetchAvalanceGeographicData.rejected]: (state, action) => {
      state.status.geographicData = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchGeograhpicAreaLines.pending]: (state) => {
      state.status.geographicAreaLines = STATE_STATUS.LOADING;
    },
    [fetchGeograhpicAreaLines.fulfilled]: (state, action) => {
      state.status.geographicAreaLines = STATE_STATUS.SUCCESS;
      state.geographicAreaLines = action.payload;
    },
    [fetchGeograhpicAreaLines.rejected]: (state, action) => {
      state.status.geographicAreaLines = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchGeograhpicAreaPoints.pending]: (state) => {
      state.status.geographicAreaPoints = STATE_STATUS.LOADING;
    },
    [fetchGeograhpicAreaPoints.fulfilled]: (state, action) => {
      state.status.geographicAreaPoints = STATE_STATUS.SUCCESS;
      state.geographicAreaPoints = action.payload;
    },
    [fetchGeograhpicAreaPoints.rejected]: (state, action) => {
      state.status.geographicAreaPoints = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
  },
});
